import { PermissionCategories } from '@/features/settings/constants/permissions';
import {
  IAppRole,
} from "@/types";
import {
  InternalPermissionState,
  Permission,
} from '@/types/permissions.types';
import { Badge, Button, Card, Select, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React from 'react';
import {
  FaCheckCircle,
  FaFileExcel,
  FaFilePdf,
  FaFolderOpen,
  FaInfoCircle,
  FaTimesCircle,
} from 'react-icons/fa';

const { Title, Paragraph, Text } = Typography;

interface PermissionsMatrixProps {
  editable?: boolean;
  showHeader?: boolean;
  permissionState?: InternalPermissionState;
  customRoles?: IAppRole[];
  selectedCategory?: string;
  onPermissionToggle?: (role: any | string, permission: Permission) => void;
  onRoleHeaderClick?: (role: IAppRole) => void;
}

interface PermissionRowData {
  key: string;
  permissionId: Permission;
  permissionLabel: string;
  permissionDescription: string;
  category: string;
  isCategory?: boolean;
  [key: string]: any;
}

export const PermissionsMatrixAntd: React.FC<PermissionsMatrixProps> = ({
  editable = false,
  showHeader = true,
  permissionState,
  customRoles = [],
  selectedCategory: selectedCategoryProp = 'all',
  onPermissionToggle,
  onRoleHeaderClick,
}) => {
  const [selectedCategory, setSelectedCategory] = React.useState(selectedCategoryProp);
  // Helper function to check if permission is enabled
  const isPermissionEnabled = (role: any | string, permission: Permission): boolean => {
    if (!permissionState) {
      return false;
    }

    // Check in permission state
    if (permissionState[role]?.[permission] !== undefined) {
      return permissionState[role][permission].enabled;
    }


    return false;
  };

  // Helper function to check if permission is modified
  const isPermissionModified = (role: any | string, permission: Permission): boolean => {
    if (!permissionState) return false;
    return permissionState[role]?.[permission]?.modified ?? false;
  };

  const filteredCategories =
    selectedCategory === 'all'
      ? PermissionCategories
      : PermissionCategories.filter((cat) => cat.id === selectedCategory);

  const handleExportToExcel = () => {
    const rows: string[] = [];

    // Build header with custom roles
    const headerRow = [
      'Permission',
      ...customRoles.map((role) => role.dogma_name),
    ];
    rows.push(headerRow.join(','));

    PermissionCategories.forEach((category) => {
      rows.push(`"${category.name}"`);
      category.permissions.forEach((perm) => {
        const row = [`"${perm.label}"`];
        customRoles.forEach((customRole) => {
          const roleKey = customRole.dogma_approleid;
          const hasPermission = isPermissionEnabled(roleKey, perm.id);
          row.push(hasPermission ? 'Yes' : 'No');
        });
        rows.push(row.join(','));
      });
      rows.push('');
    });

    const csv = rows.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'victorian-sliders-permissions-matrix.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportToPDF = () => {
    window.print();
  };

  // Prepare table data
  const tableData: PermissionRowData[] = [];
  filteredCategories.forEach((category) => {
    // Add category header row
    tableData.push({
      key: `category-${category.id}`,
      permissionId: '' as Permission,
      permissionLabel: category.name,
      permissionDescription: '',
      category: category.id,
      isCategory: true,
    });

    // Add permission rows
    category.permissions.forEach((perm) => {
      const rowData: PermissionRowData = {
        key: perm.id,
        permissionId: perm.id,
        permissionLabel: perm.label,
        permissionDescription: perm.description,
        category: category.id,
        isCategory: false,
      };

      // Add custom role permission status only
      customRoles.forEach((customRole) => {
        const roleKey = customRole.dogma_approleid;
        rowData[roleKey] = isPermissionEnabled(roleKey, perm.id);
        rowData[`${roleKey}_modified`] = isPermissionModified(roleKey, perm.id);
      });

      tableData.push(rowData);
    });
  });

  // Build columns - only custom roles from API
  const allRoles = customRoles.map((customRole) => ({
    id: customRole.dogma_approleid,
    label: customRole.dogma_name,
    isDefault: false,
    isCompanyAdmin: false,
    roleData: customRole,
  }));

  // Build columns
  const columns: ColumnsType<PermissionRowData> = [
    {
      title: 'Permission',
      dataIndex: 'permissionLabel',
      key: 'permission',
      width: 200,
      fixed: 'left',
      render: (text, record) => {
        if (record.isCategory) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaFolderOpen style={{ color: '#8b1538' }} />
              <Text strong style={{ color: '#8b1538' }}>
                {text}
              </Text>
            </div>
          );
        }
        return (
          <div>
            <div style={{ fontWeight: 500, color: '#1a1a1a' }}>{text}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
              {record.permissionDescription}
            </div>
          </div>
        );
      },
    },
    ...allRoles.map((roleInfo) => ({
      title: (
        <div
          style={{
            textAlign: 'center',
            cursor: 'pointer',
            padding: '8px 4px',
            borderRadius: '4px',
            transition: 'background-color 0.2s',
          }}
          onClick={() => onRoleHeaderClick?.(roleInfo.roleData)}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f0f0f0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          title="Click to edit role"
        >
          <div style={{ fontWeight: 600, fontSize: 13 }}>{roleInfo.label}</div>
        </div>
      ),
      dataIndex: roleInfo.id,
      key: roleInfo.id,
      width: 120,
      align: 'center' as const,
      render: (hasPermission: boolean, record: PermissionRowData) => {
        if (record.isCategory) {
          return null;
        }

        const isModified = record[`${roleInfo.id}_modified`];

        if (editable && !roleInfo.isCompanyAdmin) {
          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
              <Button
                type="text"
                size="small"
                style={{
                  color: hasPermission ? '#10b981' : '#9ca3af',
                  fontWeight: 600,
                  fontSize: 16,
                }}
                onClick={() => onPermissionToggle?.(roleInfo.id, record.permissionId)}
                title={`Toggle ${record.permissionLabel} for ${roleInfo.label}`}
              >
                {hasPermission ? <FaCheckCircle /> : <FaTimesCircle />}
              </Button>
              {isModified && (
                <Badge
                  status="processing"
                  title="Modified"
                />
              )}
            </div>
          );
        }

        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            {hasPermission ? (
              <FaCheckCircle style={{ color: '#10b981', fontSize: 16 }} />
            ) : (
              <FaTimesCircle style={{ color: '#9ca3af', fontSize: 16 }} />
            )}
            {isModified && (
              <Badge
                status="processing"
                title="Modified"
              />
            )}
          </div>
        );
      },
    })),
  ];

  return (
    <div style={{ padding: showHeader ? 24 : 0 }}>
      {/* Header */}
      {showHeader && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <Title level={2} style={{ marginBottom: 8 }}>
                Permissions Matrix
              </Title>
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Security matrix showing role-based permissions across the system
              </Paragraph>
            </div>

            <Space wrap>
              <Select
                value={selectedCategory}
                onChange={setSelectedCategory}
                style={{ width: 200 }}
                options={[
                  { value: 'all', label: 'All Categories' },
                  ...PermissionCategories.map((cat) => ({
                    value: cat.id,
                    label: cat.name,
                  })),
                ]}
              />
              <Button icon={<FaFileExcel />} onClick={handleExportToExcel}>
                Export to Excel
              </Button>
              <Button icon={<FaFilePdf />} onClick={handleExportToPDF}>
                Export to PDF
              </Button>
            </Space>
          </div>
        </Card>
      )}


      {/* Legend */}
      <Card size="small" style={{ marginBottom: 16, background: '#fafafa', borderColor: '#e5e7eb' }}>
        <Space wrap size="large">
          <Space size="small">
            <FaCheckCircle style={{ color: '#10b981', fontSize: 18 }} />
            <Text style={{ fontSize: 13 }}>Permission Granted</Text>
          </Space>
          <Space size="small">
            <FaTimesCircle style={{ color: '#9ca3af', fontSize: 18 }} />
            <Text style={{ fontSize: 13 }}>Permission Denied</Text>
          </Space>
          {editable && (
            <Space size="small">
              <FaInfoCircle style={{ color: '#3b82f6', fontSize: 18 }} />
              <Text style={{ fontSize: 13 }}>Click to toggle permissions</Text>
            </Space>
          )}
        </Space>
      </Card>

      {/* Matrix Table */}
      <Card bodyStyle={{ padding: 0 }}>
        <Table
          columns={columns}
          dataSource={tableData}
          pagination={false}
          scroll={{ x: 1400 }}
          size="middle"
          rowClassName={(record) =>
            record.isCategory ? 'category-row' : 'permission-row'
          }
          bordered
        />
        <style>{`
          .category-row {
            background: #f9fafb !important;
            font-weight: 600;
          }
          .category-row:hover {
            background: #f3f4f6 !important;
          }
          .permission-row:hover {
            background: #fafafa !important;
          }
        `}</style>
      </Card>

      {/* Critical Notes */}
      {/* <Card style={{ marginTop: 16 }} bodyStyle={{ padding: 0 }}>
        <Collapse
          defaultActiveKey={[]}
          items={[
            {
              key: 'notes',
              label: (
                <Space>
                  <FaInfoCircle style={{ color: '#8b1538' }} />
                  <Text strong>Important Permission Guidelines</Text>
                </Space>
              ),
              children: (
                <Space direction="vertical" size="small" style={{ width: '100%', padding: '8px 0' }}>
                  <Alert
                    message="Company Admin"
                    description="Has access to ALL subordinate permissions. Cannot be restricted."
                    type="info"
                    showIcon
                    style={{ marginBottom: 8 }}
                  />
                  <Alert
                    message="Credit Control Separation"
                    description="Sales team cannot access credit control functions, and vice versa."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 8 }}
                  />
                  <Alert
                    message="Single Role Policy"
                    description="Users cannot have multiple roles simultaneously."
                    type="info"
                    showIcon
                    style={{ marginBottom: 8 }}
                  />
                  <Alert
                    message="Custom Permissions"
                    description="Company Admin can grant temporary/additional permissions to any user."
                    type="success"
                    showIcon
                    style={{ marginBottom: 8 }}
                  />
                  <Alert
                    message="Onboarding Permission"
                    description="Can be assigned to Customer Service, RSM, or Admin independently."
                    type="info"
                    showIcon
                  />
                </Space>
              ),
            },
          ]}
        />
      </Card> */}
    </div>
  );
};

export default PermissionsMatrixAntd;
